<template>
  <Dialog
    :is-open="isOpen"
    title="Add New Task"
    width="400px"
    @close="closeDialog"
  >
    <div :class="$style.content">
      <div :class="$style.field">
        <label for="taskName">Task Name</label>
        <input
          id="taskName"
          v-model="taskName"
          type="text"
          placeholder="Enter task name (default: Task)"
          @keyup.enter="addTask"
        />
      </div>

      <div :class="$style.field">
        <label for="taskTopic">Topic</label>
        <TopicChipSelect
          v-model="selectedTopic"
          @update:modelValue="updateSelectedTopic"
        />
      </div>

      <div :class="$style.field">
        <label for="taskProject">Project (optional)</label>
        <ProjectSelect
          ref="projectSelectRef"
          v-model="selectedProject"
          @update:modelValue="updateSelectedProject"
        />
      </div>

      <label for="estimatedTime">Estimated Hours</label>
      <div :class="$style.estimateSection">
        <div v-if="!showExactInput" :class="$style.toggleGroup">
          <ToggleGroupRoot
            v-model="selectedEstimate"
            type="single"
            @update:model-value="handleEstimateChange"
          >
            <ToggleGroupItem value="30" :class="$style.toggleItem">
              ½
            </ToggleGroupItem>
            <ToggleGroupItem value="60" :class="$style.toggleItem">
              1
            </ToggleGroupItem>
            <ToggleGroupItem value="120" :class="$style.toggleItem">
              2
            </ToggleGroupItem>
            <ToggleGroupItem value="180" :class="$style.toggleItem">
              3
            </ToggleGroupItem>
            <ToggleGroupItem value="240" :class="$style.toggleItem">
              4
            </ToggleGroupItem>
            <ToggleGroupItem value="300" :class="$style.toggleItem">
              5
            </ToggleGroupItem>
          </ToggleGroupRoot>
        </div>

        <div v-if="showExactInput" :class="$style.exactInputs">
          <div :class="$style.timeInputs">
            <TimeInput
              id="estimatedTime"
              :duration="estimatedDurationMs"
              @update:duration="updateDurationFromTime"
              @enter="addTask"
            />
          </div>
        </div>

        <button
          :class="$style.toggleButton"
          @click="showExactInput = !showExactInput"
          type="button"
        >
          {{ showExactInput ? "Use presets" : "Use exact time" }}
        </button>
      </div>
    </div>

    <div :class="$style.actions">
      <button :class="$style.cancelButton" @click="closeDialog">Cancel</button>
      <button :class="$style.addButton" @click="addTask">Add Task</button>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import Dialog from "./Dialog.vue";
import TopicChipSelect from "./TopicChipSelect.vue";
import ProjectSelect from "./ProjectSelect.vue";
import TimeInput from "./TimeInput.vue";
import { ToggleGroupItem, ToggleGroupRoot } from "reka-ui";

const props = defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (
    e: "add-task",
    taskData: {
      name: string;
      topicId: string;
      projectId?: string;
      estimatedDuration: number;
    }
  ): void;
}>();

const taskName = ref("");
const selectedTopic = ref("");
const selectedProject = ref<string | undefined>(undefined);
const projectSelectRef = ref<InstanceType<typeof ProjectSelect> | null>(null);
const estimatedHours = ref(0);
const estimatedMinutes = ref(0);
const showExactInput = ref(false);
const selectedEstimate = ref("0");

const estimatedDurationMs = computed(() => {
  return (estimatedHours.value * 60 + estimatedMinutes.value) * 60 * 1000;
});

function closeDialog() {
  emit("close");
}

function updateSelectedTopic(topicId: string) {
  selectedTopic.value = topicId;
}

function updateSelectedProject(projectId?: string) {
  selectedProject.value = projectId;
}

function handleEstimateChange(value: any) {
  if (!value) {
    // No estimate selected
    estimatedHours.value = 0;
    estimatedMinutes.value = 0;
    return;
  }
  const minutes = parseInt(value as string);
  estimatedHours.value = Math.floor(minutes / 60);
  estimatedMinutes.value = minutes % 60;
}

function updateDurationFromTime(durationMs: number) {
  const totalMinutes = Math.floor(durationMs / (1000 * 60));
  estimatedHours.value = Math.floor(totalMinutes / 60);
  estimatedMinutes.value = totalMinutes % 60;
  // Update the toggle selection to match the new duration
  const totalMinutesInt = totalMinutes;
  if (totalMinutesInt === 30) {
    selectedEstimate.value = "30";
  } else if (totalMinutesInt === 60) {
    selectedEstimate.value = "60";
  } else if (totalMinutesInt === 120) {
    selectedEstimate.value = "120";
  } else if (totalMinutesInt === 180) {
    selectedEstimate.value = "180";
  } else if (totalMinutesInt === 300) {
    selectedEstimate.value = "300";
  } else {
    // If it doesn't match any preset or is 0, clear the selection
    selectedEstimate.value = "";
  }
}

function addTask() {
  // Ensure project is created/selected if user typed a project name
  let finalProjectId = selectedProject.value;
  if (projectSelectRef.value) {
    const ensuredProjectId = projectSelectRef.value.ensureProjectCreated();
    if (ensuredProjectId !== undefined) {
      finalProjectId = ensuredProjectId;
    }
  }

  // Use 'Task' as the default name if the user leaves it blank
  const name = taskName.value.trim() || "Task";

  const estimatedDuration =
    (estimatedHours.value * 60 + estimatedMinutes.value) * 60 * 1000;

  emit("add-task", {
    name,
    topicId: selectedTopic.value,
    projectId: finalProjectId,
    estimatedDuration,
  });

  // Reset form
  taskName.value = "";
  selectedTopic.value = "";
  selectedProject.value = undefined;
  estimatedHours.value = 0;
  estimatedMinutes.value = 0;

  closeDialog();
}

// Reset form when dialog opens
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      taskName.value = "";
      selectedTopic.value = "";
      selectedProject.value = undefined;
      estimatedHours.value = 0;
      estimatedMinutes.value = 0;
      showExactInput.value = false;
      selectedEstimate.value = "";
    }
  }
);
</script>

<style module>
.content {
  padding: 0 20px;
}

.field {
  margin-bottom: 20px;
}

.field label {
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  font-size: 0.9em;
}

.field input,
.field select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 14px;
  transition: border-color 0.2s ease;
  height: 40px;
}

.field input:focus,
.field select:focus {
  outline: none;
  border-color: var(--primary-color);
  background: rgba(255, 255, 255, 0.15);
}

.field input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.timeInputs {
  display: flex;
  align-items: center;
}

.timeInputs input {
  width: 120px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  font-size: 16px;
  font-family: inherit;
  padding: 10px 12px;
}

.timeInputs input:focus {
  outline: none;
  border-color: var(--primary-color);
  background: rgba(255, 255, 255, 0.15);
}

.toggleGroup {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.toggleItem {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 8px 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.toggleItem:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.toggleItem[data-state="on"] {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
}

.estimateSection {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toggleButton {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px 0;
  align-self: flex-start;
}

.toggleButton:hover {
  color: rgba(255, 255, 255, 0.8);
}

.exactInputs {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.actions {
  display: flex;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 20px;
}

.cancelButton,
.addButton {
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 40px;
}

.cancelButton {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.cancelButton:hover {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.addButton {
  background: var(--secondary-color);
  color: white;
}

.addButton:hover:not(:disabled) {
  background: var(--secondary-hover);
}

.addButton:disabled {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.4);
  cursor: not-allowed;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .dialog {
    width: 95%;
    margin: 10px;
  }

  .header {
    padding: 15px 15px 0 15px;
  }

  .content {
    padding: 0 15px;
  }

  .actions {
    padding: 15px;
  }

  .timeInputs input {
    width: 70px;
  }

  /* Prevent iOS input zoom by setting font-size to at least 16px */
  .field input,
  .field select {
    font-size: 16px !important;
  }
}
</style>
